import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://elitebet-api-production.up.railway.app/api';

const Profile = () => {
  const { user, loading, logout, refreshUser } = useUser();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [casinoUsername, setCasinoUsername] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState('');

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-card">
          <p className="profile-loading">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const getAvatarUrl = () => {
    if (user?.avatar && user?.discord_id) {
      return `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png?size=128`;
    }
    if (user?.avatar && user?.google_id) {
      return user.avatar;
    }
    return null;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const rewards = [
    { icon: '💵', name: t('reward1'), cost: 10000 },
    { icon: '💰', name: t('reward2'), cost: 24000 },
    { icon: '💎', name: t('reward3'), cost: 45000 },
  ];

  const openClaimModal = (reward) => {
    setSelectedReward(reward);
    setCasinoUsername('');
    setClaimError('');
    setClaimSuccess('');
    setShowClaimModal(true);
  };

  const closeClaimModal = () => {
    setShowClaimModal(false);
    setSelectedReward(null);
    setCasinoUsername('');
    setClaimError('');
  };

  const handleClaim = async () => {
    if (!casinoUsername.trim()) {
      setClaimError(t('claimErrorUsername'));
      return;
    }

    setClaimLoading(true);
    setClaimError('');

    try {
      const token = localStorage.getItem('elitebet_user_token');
      const response = await fetch(`${API_URL}/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reward_name: selectedReward.name,
          reward_cost: selectedReward.cost,
          casino_username: casinoUsername.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setClaimError(data.error || t('claimErrorConnection'));
        setClaimLoading(false);
        return;
      }

      setClaimSuccess(t('claimSuccess'));
      refreshUser();
      
      setTimeout(() => {
        closeClaimModal();
      }, 2000);

    } catch (err) {
      setClaimError(t('claimErrorConnection'));
    } finally {
      setClaimLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-card">
        
        {/* Avatar & Info */}
        <div className="profile-header">
          {getAvatarUrl() ? (
            <img src={getAvatarUrl()} alt="" className="profile-avatar" />
          ) : (
            <div className="profile-avatar-placeholder">👤</div>
          )}
          <h1 className="profile-username">{user.username}</h1>
          <p className="profile-email">{user.email}</p>
          <span className="profile-member-since">
            {t('memberSince')} {new Date(user.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Points Section */}
        <div className="profile-section">
          <div className="profile-points-box">
            <span className="profile-points-label">{t('myPoints')}</span>
            <span className="profile-points-value">{user.points.toLocaleString()}</span>
          </div>
        </div>

        {/* Rewards Section */}
        <div className="profile-section">
          <h3 className="profile-section-title">{t('availableRewards')}</h3>
          <div className="profile-rewards">
            {rewards.map((reward, index) => {
              const canClaim = user.points >= reward.cost;
              return (
                <div key={index} className={`profile-reward-item ${canClaim ? 'available' : 'locked'}`}>
                  <span className="reward-icon">{reward.icon}</span>
                  <div className="reward-details">
                    <span className="reward-name">{reward.name}</span>
                    <span className="reward-cost">{reward.cost.toLocaleString()} {t('points')}</span>
                  </div>
                  <button 
                    className="reward-claim-btn" 
                    disabled={!canClaim}
                    onClick={() => canClaim && openClaimModal(reward)}
                  >
                    {canClaim ? t('claim') : '🔒'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Logout Button */}
        <button className="profile-logout-btn" onClick={handleLogout}>
          {t('logout')}
        </button>

      </div>

      {/* Claim Modal */}
      {showClaimModal && selectedReward && (
        <div className="claim-modal-overlay" onClick={closeClaimModal}>
          <div className="claim-modal" onClick={(e) => e.stopPropagation()}>
            <button className="claim-modal-close" onClick={closeClaimModal}>✕</button>
            
            <div className="claim-modal-header">
              <span className="claim-modal-icon">{selectedReward.icon}</span>
              <h2 className="claim-modal-title">{t('claimTitle')} {selectedReward.name}</h2>
              <p className="claim-modal-cost">{selectedReward.cost.toLocaleString()} {t('points')}</p>
            </div>

            {claimSuccess ? (
              <div className="claim-success">
                <span className="claim-success-icon">✓</span>
                <p>{claimSuccess}</p>
              </div>
            ) : (
              <>
                <div className="claim-modal-body">
                  <label className="claim-label">{t('claimCasinoLabel')}</label>
                  <input
                    type="text"
                    className="claim-input"
                    placeholder={t('claimCasinoPlaceholder')}
                    value={casinoUsername}
                    onChange={(e) => setCasinoUsername(e.target.value)}
                    autoFocus
                  />
                  <p className="claim-hint">{t('claimHint')}</p>
                  
                  {claimError && (
                    <div className="claim-error">{claimError}</div>
                  )}
                </div>

                <div className="claim-modal-footer">
                  <button className="claim-cancel-btn" onClick={closeClaimModal}>
                    {t('claimCancel')}
                  </button>
                  <button 
                    className="claim-confirm-btn" 
                    onClick={handleClaim}
                    disabled={claimLoading}
                  >
                    {claimLoading ? t('claimSending') : t('claimConfirm')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
